import { useEffect, useMemo, useState } from 'react';
import { Card } from '@iwoca/orion';
import { InformationMessage } from 'components/InformationMessage/InformationMessage';
import { useOffers } from 'components/Signup/hooks/useOffers';
import { TradingHistoryTitle } from 'components/Signup/TradingHistory/TradingHistoryTitle/TradingHistoryTitle';
import { useAmplitude } from 'hooks/useAmplitude';
import { useLocale } from 'hooks/useLocale';
import { TOfferDataRequirement } from 'lendingApi/lapiOverrides.types';
import _ from 'lodash';
import { useNavigate } from 'react-router-dom';
import { REQUESTED_AMOUNT_CONTACT_THRESHOLD } from 'utils/applications/signup.config';
import { PATH_NAMES } from 'utils/routes/pathNames';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import { useSignupData } from '../hooks/useSignupData';
import { DocumentUploadInfo } from './DocumentUploadInfo/DocumentUploadInfo';
import { LoanPurposeDetails } from './LoanPurposeDetails/LoanPurposeDetails';
import { AgedDebtorReport } from './RequirementTypes/AgedDebtorReport';
import { BankStatements } from './RequirementTypes/BankStatements/BankStatements';
import { BusinessPlan } from './RequirementTypes/BusinessPlan';
import { CompanyAccounts } from './RequirementTypes/CompanyAccounts';
import { ManagementAccounts } from './RequirementTypes/ManagementAccounts';
import { OtherDocuments } from './RequirementTypes/OtherDocuments';
import { VATReturns } from './RequirementTypes/VATReturns';
import { SubmitDocuments } from './SubmitDocuments/SubmitDocuments';
import styles from './TradingHistory.module.css';
import { TradingHistoryLoader } from './TradingHistoryLoader';

type TProps = { showTitle?: boolean };

export const TradingHistory = ({ showTitle = true }: TProps) => {
  const { isUK } = useLocale();
  const navigate = useNavigate();
  const [purposeDetails, setPurposeDetails] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  const {
    applicationId,
    documentRequirements,
    isLoadingOffers,
    isRefetchingOffers,
    offers,
  } = useOffers({ getOffersEnabled: true });

  const isLoading =
    isLoadingOffers || (isRefetchingOffers && !documentRequirements);

  useEffect(() => {
    if (!isLoading && offers && !documentRequirements) {
      navigate(
        PATH_NAMES.application({ applicationId: applicationId as string }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, offers, documentRequirements]);

  const {
    updateLoanPurposeDetails,
    applicationData,
    stateData,
    hasPutApplicationError,
    isLoadingPutApplication,
  } = useSignupData();
  const { trackEvent } = useAmplitude();

  const amountIsAboveContactThreshold =
    Number(applicationData?.amount) >= REQUESTED_AMOUNT_CONTACT_THRESHOLD;

  const hasDocumentsRequired = useMemo(
    () =>
      Boolean(
        documentRequirements?.find((action) => {
          return action.status === 'required' && action.is_optional === false;
        }),
      ),
    [documentRequirements],
  );

  useEffect(() => {
    if (applicationData.loanPurposeDetails) {
      setPurposeDetails(applicationData.loanPurposeDetails);
    }
  }, [applicationData.loanPurposeDetails]);

  useEffect(() => {
    if (Boolean(documentRequirements) && !hasDocumentsRequired) {
      navigate(
        PATH_NAMES.application({ applicationId: applicationId as string }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Boolean(documentRequirements), hasDocumentsRequired]);

  const DOCUMENT_REQUIREMENTS: { [key: string]: React.ElementType } = {
    company_accounts: CompanyAccounts,
    management_accounts: ManagementAccounts,
    business_plan: BusinessPlan,
    aged_debtor_report: AgedDebtorReport,
    vat_return: VATReturns,
    bank_statement: BankStatements,
    via_open_banking: BankStatements,
  };

  const handleUpdateApplication = async () => {
    await updateLoanPurposeDetails(purposeDetails);
  };

  const renderDocumentRequirements = (
    requirements: TOfferDataRequirement['actions'],
  ) => {
    return requirements?.map((requirement) => {
      if (!requirement.type || !DOCUMENT_REQUIREMENTS[requirement.type]) {
        return null;
      }

      const RequirementDetails = DOCUMENT_REQUIREMENTS[requirement.type];

      return (
        <RequirementDetails
          key={requirement.type}
          isOptional={requirement.is_optional}
          quantifier={requirement.quantifier?.value}
          resolutionMethods={requirement.resolution_methods}
          status={requirement.status}
          isSubmitted={isSubmitted}
          customerDetails={{
            email: stateData?.applicant?.email,
            firstName: stateData?.applicant?.firstName,
          }}
        />
      );
    });
  };

  const renderContents = () => {
    if (isLoading) {
      return <TradingHistoryLoader />;
    }

    if (!documentRequirements) {
      return;
    }

    const optionalDocumentRequirements = documentRequirements.filter(
      (document) => document.is_optional,
    );

    const requiredDocumentRequirements = documentRequirements?.filter(
      (document) => !document.is_optional,
    );

    trackEvent(AMPLITUDE_EVENTS.documentRequirementsLoaded, {
      'Optional documents': optionalDocumentRequirements
        ?.map((doc) => _.startCase(doc.type))
        .join(', '),
      'Required documents': requiredDocumentRequirements
        ?.map((doc) => _.startCase(doc.type))
        .join(', '),
    });

    return (
      <>
        {applicationData.loanPurpose && (
          <LoanPurposeDetails
            purpose={applicationData.loanPurpose}
            purposeDetails={purposeDetails}
            setPurposeDetails={setPurposeDetails}
          />
        )}
        <Card className={styles.cardContainer}>
          <h2 className={styles.cardHeading}>Required documents</h2>
          <div>{renderDocumentRequirements(requiredDocumentRequirements)}</div>
        </Card>

        <Card className={styles.cardContainer}>
          <h2 className={styles.cardHeading}>Optional documents</h2>
          <div>
            {renderDocumentRequirements(optionalDocumentRequirements)}
            {/* Using other documents on DE will trigger manual review */}
            {/* Do not use on DE unless this is intended */}
            <OtherDocuments />
          </div>
        </Card>
        {amountIsAboveContactThreshold && isUK && (
          <InformationMessage
            alignCenter={true}
            message="For request amounts of £350,000 and above, our underwriting team may contact the applicant directly. We’ll notify you in advance."
          />
        )}
        {showTitle && <DocumentUploadInfo />}
        <SubmitDocuments
          isSubmitted={isSubmitted}
          setIsSubmitted={setIsSubmitted}
          updateApplication={handleUpdateApplication}
          isLoading={isLoadingPutApplication}
          hasError={Boolean(hasPutApplicationError)}
        />
      </>
    );
  };

  return (
    <div className={styles.tradingHistory} id="tradingHistory">
      {showTitle && <TradingHistoryTitle />}
      {renderContents()}
    </div>
  );
};
